<template>
  <div class="treeselect-container div-container">
    <validation-observer ref="refFormObserver">
      <!-- id_profile -->
      <validation-provider
        #default="validationContext"
        :name="$tc('profiles.title', 0)"
        rules="required"
      >
        <b-form-group
          class="text-capitalize"
          :label="$tc('profiles.title', 0)"
          label-for="id_profile"
          :state="getValidationState(validationContext)"
        >
          <v-select
            v-model="itemData.id_profile"
            data-cy="automatest-wizard-profile-select"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="profileOptions"
            :clearable="false"
            :reduce="(val) => val.value"
            input-id="id_profile"
          />
          <b-form-invalid-feedback
            :state="getValidationState(validationContext)"
          >
            {{ validationContext.errors[0] }}
          </b-form-invalid-feedback>
        </b-form-group>
      </validation-provider>
    </validation-observer>

    <treeselect
      v-model="selectedPermissions"
      data-cy="permission"
      :value-consists-of="valueConsistsOf"
      :disabled="itemData.id_profile ? false : true"
      :multiple="true"
      :options="permissionsOptions"
      :flat="false"
      :nested="true"
      :always-open="true"
      :max-height="250"
      :limit="3"
    />
  </div>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { ref, computed, watch, nextTick } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";

export default {
  components: {
    Treeselect,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  props: {
    propItemData: {
      type: Object,
      required: false,
    },
    profileOptions: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const isUpdatingFromProps = ref(false);
    const { refFormObserver, getValidationState } = formValidation();
    const widgetChilds = ref([]);

    const itemData = ref(JSON.parse(JSON.stringify(props.propItemData)));

    const selectedPermissions = itemData.value.permission
      ? ref(itemData.value.permission)
      : ref([]);

    const valueConsistsOf = ref("LEAF_PRIORITY");
    const allSelected = computed(() => {
      const allIds = permissionsOptions.value.flatMap((subject) =>
        subject.children.map((child) => child.id)
      );
      return allIds.every((id) => selectedPermissions.value.includes(id));
    });

    //We get the permissions from store
    store.dispatch("permission/getAll");
    const permissionsFromStore = computed(() => {
      return store.getters["permission/getItems"];
    });

    const permissionsOptions = computed(() => {
      const subjects = new Map();
      let workspaceVisualMenuWidgetsChild = null;

      //Add each permission depending on the subject(resource)
      permissionsFromStore.value.forEach((permission) => {
        if (!subjects.has(permission.subject)) {
          subjects.set(permission.subject, {
            id: permission.subject,
            label: permission.subject,
            children: [],
            // isDisabled: true,
          });
        }
        const subject = subjects.get(permission.subject);
        subject.children.push({
          id: permission.id,
          label: permission.description,
          isDisabled: true,
        });
      });

      // Order childs alphabetical
      subjects.forEach((subject) => {
        subject.children.sort((a, b) => a.label.localeCompare(b.label));
      });

      //Search for the Workspace and child
      subjects.forEach((subject, key) => {
        if (key === "Workspace") {
          subject.children.forEach((child) => {
            if (child.id === 288) {
              //get the child in this case 288 is show menu widgets
              workspaceVisualMenuWidgetsChild = child;
              if (!workspaceVisualMenuWidgetsChild.children) {
                //create the children if it doesnt have already
                workspaceVisualMenuWidgetsChild.children = [];
              }
            }
          });
        }
      });

      //Move Widget to WorkSpace child
      if (workspaceVisualMenuWidgetsChild && subjects.has("Widget")) {
        const widgetSubject = subjects.get("Widget");
        if (widgetSubject.children) {
          workspaceVisualMenuWidgetsChild.children =
            workspaceVisualMenuWidgetsChild.children.concat(
              widgetSubject.children
            );
          //to value later in adding parent
          widgetChilds.value = workspaceVisualMenuWidgetsChild.children.map(
            (child) => child.id
          );
        }
        subjects.delete("Widget"); //Deletes the Subject Widget
      }

      //return it like a array so the library can use it and sort it
      return Array.from(subjects.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    });

    const toggleAll = () => {
      if (allSelected.value) {
        selectedPermissions.value = [];
      } else {
        selectedPermissions.value = permissionsOptions.value.flatMap(
          (subject) =>
            subject.children.flatMap((child) =>
              [child.id].concat(
                //id de los hijos del hijo
                child.children
                  ? child.children.map((grandchild) => grandchild.id)
                  : []
              )
            )
        );
      }
    };

    const validateStep = () => {
      return refFormObserver.value.validate();
    };

    watch(
      () => itemData.value.id_profile,
      (newValue, oldValue) => {
        if (newValue !== oldValue && !isUpdatingFromProps.value) {
          //finds the object of profile in profileOptions based on the newValue
          const profileObj = props.profileOptions.find(
            (profile) => profile.id === newValue
          );

          if (profileObj && profileObj.permissions) {
            //Extracts the ids of the permissions from the profile found
            selectedPermissions.value = profileObj.permissions.map(
              (perm) => perm.id
            );
          }
        }
      }
    );

    watch(
      () => props.propItemData,
      (newValue) => {
        isUpdatingFromProps.value = true;
        selectedPermissions.value = newValue.permission ? [...newValue.permission] : [];
      },
      { deep: true }
    );

    watch(
      () => selectedPermissions.value,
      (newValue, oldValue) => {
        //this is to validate when the update comes from props.
        if (newValue !== oldValue && !isUpdatingFromProps.value) {
          // Verificar si alguno de los IDs de los hijos está en la nueva lista y el ID del padre no está.
          const childIds = widgetChilds.value;
          const parentId = 288;

          const hasChildSelected = childIds.some((id) => newValue.includes(id));
          const hasParentSelected = newValue.includes(parentId);
          nextTick(() => {
            if (hasChildSelected && !hasParentSelected) {
              // Utilizamos nextTick para asegurarnos de que todas las mutaciones pendientes a selectedPermissions se hayan completado.

              // Es importante realizar una comprobación aquí para evitar un bucle infinito.
              if (!selectedPermissions.value.includes(parentId)) {
                newValue.push(parentId);
              }
            }
            emit("update:itemData", { permission: newValue });
          });
        }
        nextTick(() => {
          isUpdatingFromProps.value = false;
        });
      }
    );

    watch(
      () => itemData.value.id_profile,
      (newValue, oldValue) => {
        if (newValue !== oldValue && !isUpdatingFromProps.value) {
          emit("update:itemData", { id_profile: newValue });
        }
      }
    );

    return {
      refFormObserver,
      getValidationState,
      itemData,
      selectedPermissions,
      permissionsOptions,
      valueConsistsOf,
      allSelected,
      toggleAll,
      validateStep,
    };
  },
};
</script>

<style scoped>
textarea {
  background-color: white;
}

.treeselect-container {
  height: 100% !important;
}
</style>

